import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  AlertCircle,
  AlertOctagon,
  ArrowLeft,
  BarChart3,
  Banknote,
  Building,
  HardHat,
  Loader2,
  PlayCircle,
  RefreshCw,
  Star,
  Timer,
  TrendingUp,
  Wrench,
} from 'lucide-react';
import type { InterventionStatut } from '@mygmao/shared';
import { api, extractErrorMessage } from '../lib/api';
import { usePermissions } from '../hooks/usePermissions';
import { formatDuration, statutLabel } from '../components/interventions/badges';

/**
 * Page `/interventions/dashboard` (US-INT-01).
 *
 * Vue d'ensemble agrégée du module Interventions :
 *  - 6 KPI cards (Total, En cours, Urgences, Durée moyenne, Satisfaction, Coût moyen)
 *  - Top services / top équipements
 *  - Tendance 12 mois (line chart SVG inline)
 *  - Répartition par priorité (barres horizontales)
 *  - Répartition par statut (barres horizontales)
 */

interface Stats {
  total: number;
  by_statut: Array<{ statut: string; count: number }>;
  by_priorite: Array<{ priorite: string; count: number }>;
  urgences_ouvertes: number;
  duree_moyenne_minutes: number | null;
  taux_satisfaction: number | null;
  nb_evaluations: number;
  cout_moyen?: number | null;
  top_equipements: Array<{
    id: string | null;
    code_equipement?: string;
    designation?: string;
    count: number;
  }>;
  top_services: Array<{
    id: string | null;
    code?: string;
    nom_complet?: string;
    count: number;
  }>;
  tendance_mensuelle: Array<{ mois: string; count: number }>;
}

const PRIORITE_COLORS: Record<string, string> = {
  P0: 'bg-red-700',
  P1: 'bg-red-500',
  P2: 'bg-orange-500',
  P3: 'bg-amber-500',
  P4: 'bg-blue-500',
  P5: 'bg-gray-500',
  P6: 'bg-gray-500',
  P7: 'bg-gray-500',
};

const STATUT_COLORS: Record<string, string> = {
  nouveau: 'bg-blue-500',
  en_attente_validation: 'bg-amber-500',
  affecte: 'bg-indigo-500',
  planifie: 'bg-cyan-500',
  en_cours: 'bg-teal-500',
  en_attente_pieces: 'bg-orange-500',
  en_attente_validation_finale: 'bg-purple-500',
  cloture: 'bg-green-500',
  annule: 'bg-gray-500',
};

export function InterventionDashboardPage() {
  const { can } = usePermissions();
  const canRead = can('interventions', 'read');

  const [stats, setStats] = useState<Stats | null>(null);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  async function refresh() {
    setLoading(true);
    setErrorMessage(null);
    try {
      const res = await api.get<{ data: Stats }>('/interventions/stats');
      setStats(res.data.data);
    } catch (err) {
      setErrorMessage(extractErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (!canRead) return;
    refresh();
  }, [canRead]);

  const enCoursCount = useMemo(() => {
    if (!stats) return 0;
    const inProgress: InterventionStatut[] = ['en_cours', 'planifie', 'en_attente_pieces'];
    return stats.by_statut
      .filter((s) => inProgress.includes(s.statut as InterventionStatut))
      .reduce((acc, s) => acc + s.count, 0);
  }, [stats]);

  if (!canRead) {
    return (
      <div className="max-w-7xl mx-auto p-6">
        <p className="text-sm text-gray-500">Vous n'avez pas accès au module Interventions.</p>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto space-y-4">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <Link
            to="/interventions"
            className="inline-flex items-center text-xs text-gray-500 hover:text-gray-800 mb-1"
          >
            <ArrowLeft className="w-3 h-3 mr-1" />
            Retour aux interventions
          </Link>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center">
            <BarChart3 className="w-7 h-7 mr-2 text-primary-600" />
            Dashboard Interventions
          </h1>
          <p className="text-sm text-gray-500 mt-1">Vue d'ensemble agrégée — US-INT-01</p>
        </div>
        <button
          onClick={refresh}
          className="inline-flex items-center p-2 border border-gray-300 rounded-lg text-gray-700 bg-white hover:bg-gray-50"
          title="Rafraîchir"
        >
          <RefreshCw className="w-4 h-4" />
        </button>
      </div>

      {errorMessage && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3 flex items-start space-x-2">
          <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-red-700">{errorMessage}</p>
        </div>
      )}

      {loading && !stats ? (
        <div className="flex items-center justify-center p-24 bg-white rounded-lg shadow-sm border border-gray-200">
          <Loader2 className="w-10 h-10 animate-spin text-primary-500" />
        </div>
      ) : !stats ? (
        <p className="text-sm text-gray-500">Aucune donnée disponible.</p>
      ) : (
        <>
          {/* KPI cards (6) */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <KpiCard
              label="Total interventions"
              value={String(stats.total)}
              icon={HardHat}
              tone="primary"
            />
            <KpiCard label="En cours" value={String(enCoursCount)} icon={PlayCircle} tone="teal" />
            <KpiCard
              label="Urgences ouvertes"
              value={String(stats.urgences_ouvertes)}
              icon={AlertOctagon}
              tone="red"
              to="/interventions/urgences"
            />
            <KpiCard
              label="Durée moyenne"
              value={formatDuration(stats.duree_moyenne_minutes)}
              icon={Timer}
              tone="purple"
            />
            <SatisfactionCard taux={stats.taux_satisfaction} nbEvaluations={stats.nb_evaluations} />
            <KpiCard
              label="Coût moyen"
              value={typeof stats.cout_moyen === 'number' ? formatCurrency(stats.cout_moyen) : '—'}
              icon={Banknote}
              tone="orange"
            />
          </div>

          {/* Top services / Top équipements */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <TopList
              title="Top services"
              icon={Building}
              items={stats.top_services.slice(0, 5).map((s) => ({
                key: s.id ?? '_',
                label: s.code
                  ? `${s.code} — ${s.nom_complet ?? ''}`
                  : (s.nom_complet ?? 'Non rattaché'),
                count: s.count,
              }))}
              emptyMessage="Aucun service avec interventions sur la période."
            />
            <TopList
              title="Top équipements"
              icon={Wrench}
              items={stats.top_equipements.slice(0, 5).map((e) => ({
                key: e.id ?? '_',
                label: e.code_equipement
                  ? `${e.code_equipement} — ${e.designation ?? ''}`
                  : (e.designation ?? 'Non rattaché'),
                count: e.count,
              }))}
              emptyMessage="Aucun équipement avec interventions sur la période."
            />
          </div>

          {/* Tendance 12 mois */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-gray-900 flex items-center">
                <TrendingUp className="w-4 h-4 mr-2 text-primary-600" />
                Tendance 12 derniers mois
              </h3>
            </div>
            <TrendChart data={stats.tendance_mensuelle} />
          </div>

          {/* Répartition par priorité + statut */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <DistributionPanel
              title="Répartition par priorité"
              items={stats.by_priorite.map((p) => ({
                key: p.priorite,
                label: p.priorite,
                count: p.count,
                colorClass: PRIORITE_COLORS[p.priorite] ?? 'bg-gray-400',
              }))}
            />
            <DistributionPanel
              title="Répartition par statut"
              items={stats.by_statut.map((s) => ({
                key: s.statut,
                label: statutLabel(s.statut as InterventionStatut),
                count: s.count,
                colorClass: STATUT_COLORS[s.statut] ?? 'bg-gray-400',
              }))}
            />
          </div>
        </>
      )}
    </div>
  );
}

function formatCurrency(value: number): string {
  try {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
      maximumFractionDigits: 0,
    }).format(value);
  } catch {
    return `${value.toFixed(0)} €`;
  }
}

function KpiCard({
  label,
  value,
  icon: Icon,
  tone,
  to,
}: {
  label: string;
  value: string;
  icon: typeof HardHat;
  tone: 'primary' | 'green' | 'red' | 'orange' | 'purple' | 'teal';
  to?: string;
}) {
  const tones: Record<typeof tone, string> = {
    primary: 'text-primary-600',
    green: 'text-green-600',
    red: 'text-red-600',
    orange: 'text-orange-600',
    purple: 'text-purple-600',
    teal: 'text-teal-600',
  };
  const content = (
    <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm hover:shadow transition-shadow">
      <div className="flex items-center justify-between">
        <p className="text-xs uppercase tracking-wide text-gray-500">{label}</p>
        <Icon className={`w-4 h-4 ${tones[tone]}`} />
      </div>
      <p className={`text-2xl font-bold mt-1 ${tones[tone]}`}>{value}</p>
    </div>
  );
  return to ? <Link to={to}>{content}</Link> : content;
}

function SatisfactionCard({ taux, nbEvaluations }: { taux: number | null; nbEvaluations: number }) {
  // `taux` peut être soit une note moyenne /5 soit un pourcentage. On normalise
  // sur 5 si la valeur dépasse 5 (le serveur renvoie alors un %).
  const note5 =
    taux === null || taux === undefined
      ? null
      : taux > 5
        ? Number((taux / 20).toFixed(1))
        : Number(taux.toFixed(1));

  return (
    <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
      <div className="flex items-center justify-between">
        <p className="text-xs uppercase tracking-wide text-gray-500">Satisfaction</p>
        <Star className="w-4 h-4 text-yellow-500" />
      </div>
      {note5 === null ? (
        <p className="text-2xl font-bold mt-1 text-gray-400">—</p>
      ) : (
        <>
          <p className="text-2xl font-bold mt-1 text-yellow-600">{note5.toFixed(1)}/5</p>
          <div className="flex items-center mt-1.5">
            {[1, 2, 3, 4, 5].map((i) => (
              <Star
                key={i}
                className={`w-3.5 h-3.5 ${
                  i <= Math.round(note5) ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'
                }`}
              />
            ))}
            <span className="ml-2 text-xs text-gray-500">({nbEvaluations} avis)</span>
          </div>
        </>
      )}
    </div>
  );
}

function TopList({
  title,
  icon: Icon,
  items,
  emptyMessage,
}: {
  title: string;
  icon: typeof HardHat;
  items: Array<{ key: string; label: string; count: number }>;
  emptyMessage: string;
}) {
  const max = items.reduce((m, it) => Math.max(m, it.count), 0);
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
      <h3 className="text-sm font-semibold text-gray-900 flex items-center mb-3">
        <Icon className="w-4 h-4 mr-2 text-primary-600" />
        {title}
      </h3>
      {items.length === 0 ? (
        <p className="text-xs text-gray-500 py-4 text-center">{emptyMessage}</p>
      ) : (
        <ul className="space-y-2">
          {items.map((item, idx) => (
            <li key={item.key + idx} className="text-sm">
              <div className="flex items-center justify-between mb-0.5">
                <span className="text-gray-700 truncate pr-2" title={item.label}>
                  {idx + 1}. {item.label}
                </span>
                <span className="font-semibold text-gray-900 flex-shrink-0">{item.count}</span>
              </div>
              <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                <div
                  className="h-full bg-primary-500 rounded-full"
                  style={{ width: `${max > 0 ? (item.count / max) * 100 : 0}%` }}
                />
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function DistributionPanel({
  title,
  items,
}: {
  title: string;
  items: Array<{ key: string; label: string; count: number; colorClass: string }>;
}) {
  const total = items.reduce((acc, it) => acc + it.count, 0);
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
      <h3 className="text-sm font-semibold text-gray-900 mb-3">{title}</h3>
      {items.length === 0 || total === 0 ? (
        <p className="text-xs text-gray-500 py-4 text-center">Aucune donnée.</p>
      ) : (
        <ul className="space-y-2">
          {items.map((item) => {
            const pct = total > 0 ? (item.count / total) * 100 : 0;
            return (
              <li key={item.key}>
                <div className="flex items-center justify-between text-xs mb-0.5">
                  <span className="font-medium text-gray-700">{item.label}</span>
                  <span className="text-gray-600">
                    {item.count} · {pct.toFixed(0)}%
                  </span>
                </div>
                <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full ${item.colorClass}`}
                    style={{ width: `${pct}%` }}
                  />
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

/** Line chart minimaliste basé sur du SVG inline (pas de dépendance externe). */
function TrendChart({ data }: { data: Array<{ mois: string; count: number }> }) {
  if (data.length === 0) {
    return (
      <p className="text-xs text-gray-500 py-8 text-center">
        Aucune donnée sur les 12 derniers mois.
      </p>
    );
  }

  const W = 720;
  const H = 200;
  const PAD_L = 36;
  const PAD_R = 12;
  const PAD_T = 12;
  const PAD_B = 28;

  const innerW = W - PAD_L - PAD_R;
  const innerH = H - PAD_T - PAD_B;
  const maxCount = data.reduce((m, d) => Math.max(m, d.count), 0);
  const yMax = maxCount === 0 ? 1 : maxCount;

  const xStep = data.length > 1 ? innerW / (data.length - 1) : innerW;

  const points = data.map((d, i) => {
    const x = PAD_L + (data.length > 1 ? i * xStep : innerW / 2);
    const y = PAD_T + innerH - (d.count / yMax) * innerH;
    return { x, y, d };
  });

  const path = points
    .map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x.toFixed(1)} ${p.y.toFixed(1)}`)
    .join(' ');
  const areaPath =
    path +
    ` L ${points[points.length - 1]!.x.toFixed(1)} ${(PAD_T + innerH).toFixed(1)}` +
    ` L ${points[0]!.x.toFixed(1)} ${(PAD_T + innerH).toFixed(1)} Z`;

  // Y-axis ticks (0, max/2, max).
  const ticks = [0, Math.round(yMax / 2), yMax];

  return (
    <div className="overflow-x-auto">
      <svg
        viewBox={`0 0 ${W} ${H}`}
        className="w-full h-48"
        preserveAspectRatio="none"
        role="img"
        aria-label="Tendance mensuelle des interventions"
      >
        {/* Grille horizontale */}
        {ticks.map((t) => {
          const y = PAD_T + innerH - (t / yMax) * innerH;
          return (
            <g key={t}>
              <line
                x1={PAD_L}
                x2={W - PAD_R}
                y1={y}
                y2={y}
                stroke="#e5e7eb"
                strokeDasharray="3,3"
              />
              <text
                x={PAD_L - 6}
                y={y + 3}
                textAnchor="end"
                className="fill-gray-400"
                style={{ fontSize: 10 }}
              >
                {t}
              </text>
            </g>
          );
        })}

        {/* Aire de fond */}
        <path d={areaPath} fill="rgb(59 130 246 / 0.12)" />

        {/* Ligne principale */}
        <path
          d={path}
          fill="none"
          stroke="rgb(59 130 246)"
          strokeWidth="2"
          strokeLinejoin="round"
          strokeLinecap="round"
        />

        {/* Points */}
        {points.map((p, i) => (
          <g key={i}>
            <circle cx={p.x} cy={p.y} r="3" fill="rgb(59 130 246)" />
            <title>
              {p.d.mois} : {p.d.count}
            </title>
          </g>
        ))}

        {/* Labels X */}
        {points.map((p, i) => {
          const show =
            data.length <= 12 ||
            i === 0 ||
            i === points.length - 1 ||
            i % Math.ceil(data.length / 6) === 0;
          if (!show) return null;
          return (
            <text
              key={i}
              x={p.x}
              y={H - 8}
              textAnchor="middle"
              className="fill-gray-500"
              style={{ fontSize: 10 }}
            >
              {p.d.mois}
            </text>
          );
        })}
      </svg>
    </div>
  );
}
